/**
 * PostMediaManager — F-132 human-in-the-loop media affordances for the post
 * inspector (PostBody). Controlled, mostly-presentational strip that lets a
 * creator manage a post's media WITHOUT leaving the inspector or routing
 * through chat:
 *
 *   - add media   (upload a file → generated-content bucket → append URL)
 *   - remove media (× per thumbnail)
 *   - reorder      (‹ › per thumbnail — honest, accessible, no drag lib needed
 *                   for the 1-10 items a post realistically carries)
 *   - edit / vary  (per-image prompt → /api/generate-image edit mode → swaps
 *                   the result into that slot; the same proven Wave-5 in-place
 *                   iteration path the image inspector uses)
 *
 * Persistence is the parent's job: every mutation calls `onChange(nextUrls)`,
 * which PostBody routes through postService.updatePost({ media_urls }) and
 * invalidates the calendar/drafts/library/project query surfaces. Keeping the
 * network ops (upload, iterate) here but the DB write in the parent keeps this
 * component testable and lets the showcase mount it with no-op handlers.
 */
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  FileTextIcon,
  FilmSlateIcon,
  ImageSquareIcon,
  ImagesSquareIcon,
  PencilSimpleIcon,
  PlusIcon,
  SparkleIcon,
  UploadSimpleIcon,
  VideoCameraIcon,
  XIcon,
} from '@phosphor-icons/react';
import { isLinkedInDocumentUrl, linkedInDocumentTitleFromUrl } from '@shared/linkedin-media-rules';
import { Suspense, forwardRef, lazy, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'sonner';

import { AltTextField } from './AltTextField';
import { MediaCompareSlot } from './MediaCompareSlot';
import { VariationCandidates } from './VariationCandidates';
import { compareModeForStatus } from './compareMode';
import { predictEditTier } from './predictEditTier';
import { type UsePostMediaEditArgs, usePostMediaEdit } from './usePostMediaEdit';
import { type VideoSlotActionId, resolveVideoSlotActions } from './videoSlotActions';

import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { isVideoUrl } from '@/lib/posts/is-video-url';
import { QUIET_ACTION } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import {
  DOCUMENT_ACCEPT,
  isDocumentFile,
  uploadPostDocument,
} from '@/services/media/documentUpload';

// Lazy: MediaDrawer pulls in the media-library grid + Google Drive tab, which
// most post-inspector opens never touch (the "from library" path is opt-in).
const MediaDrawer = lazy(() => import('@/components/create/studio/MediaDrawer'));

// Floating chip background for each corner action on a media thumbnail —
// contrast-safe against ANY thumbnail content, unlike a bare icon with no
// background (which disappears against light/busy photos — measured live).
const THUMB_ACTION_CHIP = 'bg-background/85 shadow-elevation-1 backdrop-blur-sm';

/** Real edit modes the /api/generate-image route supports (not fake inpaint prompts). */
type ImageEditMode = 'inpaint' | 'outpaint' | 'recolor' | 'enhance' | 'background-replace';

/**
 * One-tap edit presets. Each fires a REAL edit mode (background-replace → FAL
 * FLUX; enhance/recolor/outpaint → the Gemini multi-ref edit path), replacing
 * the old chips that sent inpaint *prompts* labelled "Remove background" /
 * "Brighten" but actually ran plain inpaint.
 */
const EDIT_PRESETS: Array<{ label: string; editMode: ImageEditMode; prompt: string }> = [
  {
    label: 'Replace background',
    editMode: 'background-replace',
    prompt:
      'Replace the background with a clean, on-brand studio backdrop; keep the subject identical.',
  },
  {
    label: 'Enhance',
    editMode: 'enhance',
    prompt: 'Enhance lighting, sharpness and color vibrancy while keeping the subject identical.',
  },
  {
    label: 'Extend frame',
    editMode: 'outpaint',
    prompt: 'Extend the scene outward on all sides, continuing it naturally.',
  },
  {
    label: 'Recolor',
    editMode: 'recolor',
    prompt: 'Shift to a warmer, more cohesive on-brand color palette.',
  },
];

// ---------------------------------------------------------------------------
// Pure helpers — exported for unit tests (no React deps)
// ---------------------------------------------------------------------------

export interface IterateSlotRequestBody {
  postId: string;
  replaceSlot: number;
  mediaWriteMode: 'replace-slot';
  referenceImageUrl: string;
  referenceImageRoles?: string[];
  editMode: ImageEditMode;
  prompt: string;
  aspectRatio: string;
  imageModel: 'auto';
}

/**
 * Pure builder for the /api/generate-image server write-back body used by
 * `iterateSlot`. Extracted so it can be unit-tested without DOM or React.
 */
export function buildIterateSlotRequest(args: {
  postId: string;
  slotIndex: number;
  slotUrl: string;
  keepIdentity: boolean;
  existingRoles?: string[];
  editMode: ImageEditMode;
  prompt: string;
  aspectRatio: string;
}): IterateSlotRequestBody {
  const existing = args.existingRoles ?? [];
  const roles =
    existing.length > 0 || args.keepIdentity
      ? [...existing, ...(args.keepIdentity ? ['person'] : [])]
      : undefined;
  return {
    postId: args.postId,
    replaceSlot: args.slotIndex,
    mediaWriteMode: 'replace-slot',
    referenceImageUrl: args.slotUrl,
    ...(roles ? { referenceImageRoles: roles } : {}),
    editMode: args.editMode,
    prompt: args.prompt,
    aspectRatio: args.aspectRatio,
    imageModel: 'auto',
  };
}

/**
 * Gating predicate: returns true when the ✨ EDIT affordance should be shown
 * for a given post status. Scheduled posts are gated until Phase 3 (cron race
 * fix — compare-mode UI not yet built).
 *
 * // scheduled image editing gated until Phase 3 (cron race fix)
 */
export function shouldShowImageEdit(status: string | null | undefined): boolean {
  return compareModeForStatus(status) !== 'compare';
}

/**
 * Inverse alias — true when the edit affordance is HIDDEN (gated).
 * Exported for backward-compatible test coverage alongside shouldShowImageEdit.
 */
export function isImageEditGated(status: string | null | undefined): boolean {
  return !shouldShowImageEdit(status);
}

// ---------------------------------------------------------------------------
// Component props
// ---------------------------------------------------------------------------

interface PostMediaManagerProps {
  mediaUrls: string[];
  /** The post already shows an article card, so having no media is not a gap to point out. */
  hasLinkCard?: boolean;
  /** Stable child IDs from the canonical manifest. Falls back to index only for legacy rows. */
  childIds?: string[];
  selectedIndex?: number;
  onSelectChild?: (index: number) => void;
  /** When false the strip is read-only (published posts). */
  editable: boolean;
  /** Persist the next media list. Parent owns the DB write + cache invalidation. */
  onChange: (
    next: string[],
    uploaded?: { mediaType: 'image' | 'video'; coverImageUrl: string | null },
  ) => void | Promise<void>;
  /** Supabase access token — required for upload + image iteration. */
  accessToken: string | null | undefined;
  /** Owning user id — namespaces uploaded files + post-surface invalidation. */
  userId: string | null | undefined;
  /** Aspect-ratio hint passed to the edit route so iterations keep the frame. */
  aspectRatio?: string;
  /** The post ID — the server write-back key for the AI-edit/variation path. */
  postId: string;
  /** Post status string (e.g. 'draft' | 'scheduled' | 'published') — decides
   *  compare-vs-instant (scheduled → Original⇄Revised compare) via the hook. */
  status: string | null | undefined;
  /** The post's media_keys jsonb — carries media_keys.history[] (edit lineage)
   *  and media_keys.hasPerson (the generation-time person signal). */
  mediaKeys: unknown;
  /** CAS guard (Task 13/38): the row's last-known updated_at so every postId
   *  write-back can be rejected by the route if the row was mutated meanwhile. */
  expectedUpdatedAt?: string;
  /** BI-4-05 fixes-as-found: the active brand kit's guidelines, forwarded to
   *  /api/generate-image so this surface (the deepest board/post-media image
   *  integration) actually applies brand styling. Previously omitted entirely
   *  — every generate/edit/vary call here produced non-branded images
   *  regardless of the active kit. This surface has no ON/OFF toggle UI, so
   *  brand guidelines are applied whenever a usable kit is present (mirrors
   *  pre-toggle behavior for this non-composer surface). */
  brandKit?: Record<string, unknown> | null;
  /** PostBody's serialized media_keys committer, forwarded to
   *  usePostMediaEdit so the history archiver merges into fresh server keys
   *  through the shared commit queue (pre-merge review finding #4). */
  commitMediaKeys?: UsePostMediaEditArgs['commitMediaKeys'];
  /** Alt text by media index, from the child manifest. */
  altTexts?: Array<string | undefined>;
  /** Persist one image's alt text. Omit to hide the field. */
  onAltTextChange?: (index: number, next: string) => void;
  /** True when the post goes to LinkedIn: the picker then also takes PDF, PowerPoint and Word documents. */
  allowDocuments?: boolean;
}

export interface PostMediaManagerHandle {
  openMediaPicker: (defaultTab?: 'library' | 'upload' | 'google-drive') => void;
}

export const PostMediaManager = forwardRef<PostMediaManagerHandle, PostMediaManagerProps>(
  function PostMediaManager(
    {
      hasLinkCard = false,
      mediaUrls,
      childIds,
      selectedIndex = 0,
      onSelectChild,
      editable,
      onChange,
      accessToken,
      userId,
      aspectRatio,
      postId,
      status,
      mediaKeys,
      expectedUpdatedAt,
      brandKit,
      commitMediaKeys,
      altTexts,
      onAltTextChange,
      allowDocuments = false,
    }: PostMediaManagerProps,
    ref,
  ) {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    // When set, the next file picked REPLACES the image at this index instead of
    // appending. Null = the "Add" tile (append). (Declared before the early
    // return below — rules-of-hooks.)
    const replaceIndexRef = useRef<number | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [openAddMenu, setOpenAddMenu] = useState(false);
    // "From library" — attaches an already-uploaded Library/Project media item.
    // The URL MediaDrawer hands back is already a storage URL, so this skips
    // mediaService.uploadFile entirely and appends straight through the same
    // onChange contract handleFileSelected uses on success.
    const [libraryDrawerOpen, setLibraryDrawerOpen] = useState(false);
    const [libraryDrawerDefaultTab, setLibraryDrawerDefaultTab] = useState<
      'library' | 'upload' | 'google-drive'
    >('library');
    const [editPrompt, setEditPrompt] = useState('');
    const [openEditIndex, setOpenEditIndex] = useState<number | null>(null);
    // Person signal is stored at generation time on media_keys.hasPerson (Task 43,
    // via personSignalFromGeneration) — never re-derived by a client detector. A
    // person post defaults keepIdentity ON so identity edits route to Nano Banana
    // Pro; the opt-in checkbox still lets the user override per edit.
    const defaultKeepIdentity = Boolean((mediaKeys as { hasPerson?: boolean } | null)?.hasPerson);
    const [keepIdentity, setKeepIdentity] = useState(defaultKeepIdentity);
    // "Generate a slide" — adds a NEW image cohesive with the existing ones
    // (multi-seed), so a single image becomes a carousel and a carousel grows.
    const [openGenerate, setOpenGenerate] = useState(false);
    const [generatePrompt, setGeneratePrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    // Server-authoritative media-edit brain (Task 35). Owns compare-vs-instant,
    // the pendingCompare / candidates review states, history archival, CAS, and
    // all /api/generate-image { postId } write-backs. Replaces the old local
    // iterateSlot/generateSlide state machines.
    const {
      busySlot,
      pendingCompare,
      candidates,
      editSlot,
      generateVariations,
      pickCandidate,
      keepRevised,
      revertCompare,
      dismissCandidates,
    } = usePostMediaEdit({
      postId,
      status,
      userId,
      mediaUrls,
      accessToken,
      aspectRatio,
      mediaKeys,
      expectedUpdatedAt,
      brandKit,
      commitMediaKeys,
    });

    const hasMedia = mediaUrls.length > 0;
    useImperativeHandle(
      ref,
      () => ({
        openMediaPicker: (defaultTab = 'library') => {
          setOpenAddMenu(false);
          // Same append-flow reasoning as the Add-tile's "From library"
          // button above — this imperative handle is also an append entry
          // point, so any stale replace-flow ref must not leak into it.
          replaceIndexRef.current = null;
          setLibraryDrawerDefaultTab(defaultTab);
          setLibraryDrawerOpen(true);
        },
      }),
      [],
    );
    // A slot is dimmed when a compare/candidate review targets a DIFFERENT slot,
    // so the slot-scope of the in-body toggle reads unambiguously on a carousel.
    const reviewSlot = pendingCompare?.slotIndex ?? candidates?.slotIndex ?? null;
    // Reorder/remove mutate the array by INDEX, but the pending review captured a
    // raw slotIndex at stage time and never re-syncs it to the current order —
    // reordering or removing a DIFFERENT slot mid-review would silently point
    // Keep/Pick at whatever now occupies that index, overwriting an untouched
    // slide (caught in code review, no test previously covered it). Freeze
    // reorder/remove on EVERY slot — not just the reviewed one, since the
    // reviewed slot's own index shifting is equally unsafe — until the pending
    // review resolves (Keep/Revert/Pick/Dismiss).
    const reviewLocked = reviewSlot !== null;

    // Read-only published posts still show their media so the inspector reads as
    // a complete record — just without the mutation controls.
    if (!editable) {
      if (!hasMedia) return null;
      return (
        <section className="space-y-1.5">
          <p className="text-caption font-medium text-foreground">Media</p>
          <div className="flex flex-wrap gap-2">
            {mediaUrls.map((url, i) => (
              <div
                key={`${url}-${i}`}
                className="size-20 shrink-0 overflow-hidden rounded-lg border border-border/40 bg-muted/30"
              >
                <MediaThumb url={url} />
              </div>
            ))}
          </div>
        </section>
      );
    }

    const remove = (index: number) => {
      // Belt-and-suspenders: the button is also `disabled` while a review is
      // pending, but guard the handler itself against any bypass (e.g. a
      // stale disabled prop during a re-render race).
      if (reviewLocked) return;
      // Removing the LAST media used to silently persist media_urls:[] (media_type
      // becomes null), producing a media-less post with no warning — a mistake on
      // media-required platforms (Instagram/TikTok/Pinterest). Text-only posts are
      // legitimate elsewhere, so we confirm rather than block.
      if (mediaUrls.length === 1) {
        toast('Remove the only image?', {
          description:
            'This post will have no media. Instagram, TikTok and Pinterest require an image to publish.',
          action: {
            label: 'Remove',
            onClick: () => void onChange([]),
          },
        });
        return;
      }
      void onChange(mediaUrls.filter((_, i) => i !== index));
    };

    const move = (index: number, dir: -1 | 1) => {
      if (reviewLocked) return;
      const target = index + dir;
      if (target < 0 || target >= mediaUrls.length) return;
      const next = [...mediaUrls];
      [next[index], next[target]] = [next[target], next[index]];
      void onChange(next);
    };

    const handleAddClick = () => {
      setOpenAddMenu(false);
      replaceIndexRef.current = null;
      fileInputRef.current?.click();
    };
    const handleReplaceClick = (index: number) => {
      replaceIndexRef.current = index;
      fileInputRef.current?.click();
    };

    // MediaDrawer's onSelect hands back an already-uploaded storage URL — no
    // upload step here. Mirrors handleFileSelected's replace-vs-append split:
    // a REPLACE (video "Choose from library", staged via replaceIndexRef by
    // handleVideoSlotAction below) swaps the slot in place; anything else —
    // the Add tile's "From library" — appends, same as before this task.
    const handleLibraryUrl = async (url: string) => {
      setLibraryDrawerOpen(false);
      const replaceAt = replaceIndexRef.current;
      replaceIndexRef.current = null;
      if (replaceAt != null && replaceAt < mediaUrls.length) {
        await onChange(mediaUrls.map((u, i) => (i === replaceAt ? url : u)));
        toast.success('Media replaced');
        return;
      }
      await onChange([...mediaUrls, url]);
      toast.success('Media added');
    };

    const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset the input so picking the same file twice still fires onChange.
      e.target.value = '';
      if (!file) return;
      if (!userId) {
        toast.error('Sign in to add media');
        return;
      }
      if (isDocumentFile(file) && !allowDocuments) {
        toast.error('Documents can only go to LinkedIn. Add LinkedIn to this post first.');
        return;
      }
      setIsUploading(true);
      const toastId = 'post-media-upload';
      toast.loading('Uploading…', { id: toastId });
      try {
        if (isDocumentFile(file)) {
          if (mediaUrls.length > 0) {
            throw new Error(
              'A LinkedIn document goes on its own. Remove the other media first, then add the document.',
            );
          }
          const document = await uploadPostDocument(file);
          await onChange([document.publicUrl], { mediaType: 'image', coverImageUrl: null });
          toast.success('Document added', { id: toastId });
          return;
        }
        const { mediaService } = await import('@/services/media/mediaService');
        const upload = await mediaService.uploadFile(file);
        const { publicUrl } = upload;
        const replaceAt = replaceIndexRef.current;
        replaceIndexRef.current = null;
        if (replaceAt != null && replaceAt < mediaUrls.length) {
          await onChange(
            mediaUrls.map((u, i) => (i === replaceAt ? publicUrl : u)),
            {
              mediaType: upload.mediaType,
              coverImageUrl: upload.coverImageUrl,
            },
          );
          toast.success('Media replaced', { id: toastId });
        } else {
          await onChange([...mediaUrls, publicUrl], {
            mediaType: upload.mediaType,
            coverImageUrl: upload.coverImageUrl,
          });
          toast.success('Media added', { id: toastId });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        toast.error(message, { id: toastId });
      } finally {
        setIsUploading(false);
      }
    };

    // In-place image edit / variation now lives in usePostMediaEdit (see the hook
    // wiring above): editSlot() runs the compare-vs-instant edit, generateVariations()
    // surfaces candidate renders, and the pendingCompare / candidates review panels
    // render slot-scoped below the strip. These thin wrappers close the popover and
    // clear the draft prompt, then hand off to the hook.
    const runEditPreset = (index: number, prompt: string, editMode: ImageEditMode) => {
      // Belt-and-suspenders: mirrors the remove/move handler guards above — the
      // popover's controls are also `disabled` while a review is pending, but
      // guard the handler itself against any bypass (e.g. a stale disabled prop
      // during a re-render race).
      if (reviewLocked) return;
      setOpenEditIndex(null);
      void editSlot(index, prompt, { editMode, keepIdentity });
    };
    const runDescribeEdit = (index: number) => {
      if (reviewLocked) return;
      const trimmed = editPrompt.trim();
      if (!trimmed) return;
      setOpenEditIndex(null);
      setEditPrompt('');
      void editSlot(index, trimmed, { keepIdentity });
    };
    const runNewVariation = (index: number) => {
      if (reviewLocked) return;
      setOpenEditIndex(null);
      const prompt = editPrompt.trim() || 'A fresh variation of this image.';
      setEditPrompt('');
      void generateVariations(index, prompt, 1, { keepIdentity });
    };

    // Dispatches the video slot's popover (Task 12b — F-132's remaining gap: a
    // video slot previously had NO edit affordance, only Remove → re-add).
    // Only 'replace-upload' and 'replace-library' are ever reachable: `trim`
    // is cut from this launch (see videoSlotActions.ts) and is never returned
    // by resolveVideoSlotActions while `trimAvailable` is false, so it can't
    // be dispatched here — no case exists for it, and none is needed.
    const handleVideoSlotAction = (index: number, actionId: VideoSlotActionId) => {
      // Belt-and-suspenders: mirrors the remove/move/runEditPreset guards above.
      if (reviewLocked) return;
      setOpenEditIndex(null);
      if (actionId === 'replace-upload') {
        handleReplaceClick(index);
      } else if (actionId === 'replace-library') {
        replaceIndexRef.current = index;
        setLibraryDrawerDefaultTab('library');
        setLibraryDrawerOpen(true);
      }
    };

    // Generate a NEW slide cohesive with the existing images (multi-seed). Routes
    // through the same /api/generate-image path as iterateSlot, but passes ALL
    // current image slides as `referenceImageUrls` (role 'style') and APPENDS the
    // result instead of swapping a slot — handleMediaChange then auto-promotes the
    // post to `media_type:'carousel'`. This is the in-inspector "add one more image
    // to this carousel" surface (the freeform chat couldn't do it, and StudioPanel
    // never renders for persisted posts).
    const generateSlide = async (prompt: string) => {
      const trimmed = prompt.trim();
      if (!trimmed) return;
      if (!accessToken) {
        toast.error('Sign in to generate media');
        return;
      }
      const styleRefs = mediaUrls.filter((u) => !isVideoUrl(u));
      setIsGenerating(true);
      const toastId = 'post-media-generate';
      toast.loading('Generating slide…', { id: toastId });
      try {
        const response = await fetch('/api/generate-image', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            prompt: trimmed,
            ...(styleRefs.length > 0
              ? {
                  referenceImageUrls: styleRefs,
                  referenceImageRoles: styleRefs.map(() => 'style' as const),
                }
              : {}),
            aspectRatio: aspectRatio || '4:5',
            imageModel: 'auto',
            ...(brandKit ? { brandKit } : {}),
          }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || `Generate failed (${response.status})`);
        }
        const result = await response.json();
        if (result?.state === 'error') throw new Error(result.error || 'Generate failed.');
        const newUrl = result.imageUrl || result.image_url;
        if (!newUrl) throw new Error('No image returned.');
        const previous = mediaUrls;
        await onChange([...mediaUrls, newUrl]);
        toast.success('Slide added', {
          id: toastId,
          action: { label: 'Undo', onClick: () => void onChange(previous) },
        });
        setOpenGenerate(false);
        setGeneratePrompt('');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Generate failed.';
        toast.error(message, { id: toastId });
      } finally {
        setIsGenerating(false);
      }
    };

    return (
      <section className="space-y-1.5">
        <p className="text-caption font-medium text-foreground">Media</p>
        <div className="flex flex-wrap gap-2">
          {mediaUrls.map((url, index) => {
            const video = isVideoUrl(url);
            const isDoc = isLinkedInDocumentUrl(url);
            const busy = busySlot === index;
            // Dim every slot that is NOT the one currently in compare/candidate
            // review, so the in-body toggle's slot-scope reads unambiguously.
            const dimmed = reviewSlot !== null && reviewSlot !== index;
            // A staged compare/candidate review owns the strip until it
            // resolves: Remove and Move already freeze because they reindex the
            // array, but a second edit — or a file replace — fired from this
            // popover would clobber the staged state the same way. Same lock.
            const editFrozen = busy || reviewLocked;
            return (
              <div
                key={childIds?.[index] ?? `${url}-${index}`}
                className={cn(
                  // size-20, not size-16: four corner buttons need two full rows
                  // of CircleIconButton "sm" (28px desktop / 32px mobile) without
                  // overlapping. Measured live at size-16: the top row (Edit,
                  // Remove) and bottom row (Move-left, Move-right) overlapped by
                  // ~10px on mobile, where "sm" is 32px — 64px minus two 4px
                  // insets leaves no room for two 32px rows. size-20 gives an
                  // 8px clear gap between rows on mobile, 16px on desktop.
                  //
                  // No overflow-hidden/rounded/border here — those live on the
                  // inner wrapper below. `ring-offset` paints OUTSIDE this box's
                  // own border box, and an element clips its own outer box-shadow
                  // when it also carries overflow-hidden — the selection ring was
                  // rendering pre-clipped to a sliver on whichever edge sat
                  // against the strip's wrap boundary (visual QA, 2026-09-16).
                  'group/media relative size-20 shrink-0 rounded-lg transition-opacity',
                  dimmed && 'opacity-40',
                  selectedIndex === index &&
                    'ring-2 ring-ring ring-offset-2 ring-offset-background',
                )}
                role="button"
                tabIndex={0}
                aria-label={`Select slide ${index + 1}`}
                aria-current={selectedIndex === index ? 'true' : undefined}
                onClick={() => onSelectChild?.(index)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectChild?.(index);
                  }
                }}
              >
                <div className="absolute inset-0 overflow-hidden rounded-lg border border-border/40 bg-muted/30">
                  <MediaThumb url={url} />
                </div>

                {busy ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                    <SparkleIcon className="size-4 animate-pulse text-skill" weight="duotone" />
                  </div>
                ) : null}

                {/* Four independent corners, not one shared bottom toolbar. The
                  old single pill packed Edit + a divider + Move-left +
                  Move-right into one ~60px-wide bar on a 64px thumbnail —
                  measured live, the buttons were flex-shrunk to ~14px-wide
                  slivers with edges touching (the exact "overlapping,
                  cramped" look caught in review). Each corner now holds
                  exactly one action at its full un-squished size, with its
                  own floating chip (bg + shadow) so it stays legible against
                  any thumbnail content — the old Remove button had no resting
                  chip at all (bg-transparent until hover), invisible on touch
                  where hover never fires. Edit + Remove pair on top (modify
                  this slide); Move-left + Move-right pair on the bottom
                  (reorder) — the same "distinct concerns get distinct
                  corners/edges" grammar the main preview's own counter +
                  prev/next nav already uses. ALWAYS visible (not hover-gated):
                  a hover-only reveal has no touch-device equivalent. Locked
                  (dimmed + inert) while ANY slot is under compare/candidate
                  review. */}
                {isDoc ? null : video ? (
                  <Popover
                    open={openEditIndex === index}
                    onOpenChange={(next) => setOpenEditIndex(next ? index : null)}
                  >
                    <PopoverTrigger asChild>
                      <CircleIconButton
                        size="sm"
                        ariaLabel="Edit or replace video"
                        disabled={editFrozen}
                        className={cn(
                          THUMB_ACTION_CHIP,
                          'absolute left-1 top-1',
                          editFrozen
                            ? 'pointer-events-none opacity-40'
                            : 'text-muted-foreground hover:bg-background hover:text-foreground',
                        )}
                      >
                        {/* Same pencil signifier as the image popover — this
                          slot previously had NO edit affordance at all (only
                          Remove → re-add), the exact gap F-132 spec journey
                          2.4 calls out. */}
                        <PencilSimpleIcon className="size-3" weight="regular" />
                      </CircleIconButton>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-64 space-y-0.5 p-1.5">
                      {resolveVideoSlotActions({ editFrozen, trimAvailable: false }).map(
                        (action) => {
                          const ActionIcon =
                            action.id === 'replace-library' ? FilmSlateIcon : VideoCameraIcon;
                          const row = (
                            <button
                              type="button"
                              disabled={action.disabled}
                              onClick={() => handleVideoSlotAction(index, action.id)}
                              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
                            >
                              <ActionIcon
                                className="size-4 shrink-0 text-muted-foreground"
                                weight="regular"
                              />
                              {action.label}
                            </button>
                          );
                          // A disabled row's reason must be reachable via hover
                          // AND touch — a native `title` attribute is silently
                          // suppressed on a disabled element in Chrome/Safari,
                          // and touch has no hover at all. Wrapping in a
                          // non-disabled <span> trigger is what makes the
                          // Radix tooltip actually fire.
                          if (action.disabled && action.disabledReason) {
                            return (
                              <Tooltip key={action.id}>
                                <TooltipTrigger asChild>
                                  <span tabIndex={0} className="block w-full">
                                    {row}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  {action.disabledReason}
                                </TooltipContent>
                              </Tooltip>
                            );
                          }
                          return (
                            <span key={action.id} className="block w-full">
                              {row}
                            </span>
                          );
                        },
                      )}
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Popover
                    open={openEditIndex === index}
                    onOpenChange={(next) => {
                      setOpenEditIndex(next ? index : null);
                      if (!next) {
                        setEditPrompt('');
                        // Reset to defaultKeepIdentity (not false) so identity-
                        // critical posts stay ON when the popover reopens.
                        setKeepIdentity(defaultKeepIdentity);
                      }
                    }}
                  >
                    <PopoverTrigger asChild>
                      <CircleIconButton
                        size="sm"
                        ariaLabel="Edit or vary image"
                        disabled={editFrozen}
                        className={cn(
                          THUMB_ACTION_CHIP,
                          'absolute left-1 top-1',
                          editFrozen
                            ? 'pointer-events-none opacity-40'
                            : 'text-muted-foreground hover:bg-background hover:text-foreground',
                        )}
                      >
                        {/* Pencil, not sparkle — the popover's FIRST option is
                          "Replace with a different photo" (not AI at all),
                          so a sparkle-only trigger both overclaimed "this is
                          AI" and undersold "this is where you edit this
                          image" (caught in live review). Sparkle stays
                          skill-purple deeper in, scoped to the AI section. */}
                        <PencilSimpleIcon className="size-3" weight="regular" />
                      </CircleIconButton>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72 space-y-2 p-3">
                      {/* Replace = swap in a different photo entirely (distinct
                          from the AI edits below). The "Replace image" verb that
                          previously had no home. */}
                      <button
                        type="button"
                        disabled={editFrozen}
                        onClick={() => {
                          setOpenEditIndex(null);
                          handleReplaceClick(index);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
                      >
                        <ImageSquareIcon
                          className="size-4 shrink-0 text-muted-foreground"
                          weight="regular"
                        />
                        Replace with a different photo
                      </button>
                      <div className="border-t border-border/50" />
                      <div className="flex items-center gap-1.5">
                        <SparkleIcon className="size-3 text-skill/70" weight="duotone" />
                        <span className="text-detail font-medium text-skill/80">Edit with AI</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {EDIT_PRESETS.map((preset) => (
                          <button
                            key={preset.label}
                            type="button"
                            disabled={editFrozen}
                            onClick={() => runEditPreset(index, preset.prompt, preset.editMode)}
                            className="h-7 rounded-full bg-skill/[0.07] px-2.5 text-detail text-skill-text transition-colors hover:bg-skill/15 disabled:opacity-50"
                          >
                            {preset.label}
                          </button>
                        ))}
                        {/* "New variation" — surfaces a non-destructive candidate
                            render (n:1) that only commits on the user's pick, so
                            they can try a fresh take without clobbering the slot. */}
                        <button
                          type="button"
                          disabled={editFrozen}
                          onClick={() => runNewVariation(index)}
                          className="h-7 rounded-full bg-skill/[0.07] px-2.5 text-detail text-skill-text transition-colors hover:bg-skill/15 disabled:opacity-50"
                        >
                          New variation
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <label className="flex items-center gap-1.5 text-detail text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={keepIdentity}
                            disabled={editFrozen}
                            onChange={(e) => setKeepIdentity(e.target.checked)}
                            className="size-3.5 shrink-0 rounded border-border/60 accent-skill"
                          />
                          Keep this person&apos;s identity
                        </label>
                        {predictEditTier({ keepIdentity }) === 'pro' ? (
                          <span className="rounded-full bg-skill/10 px-1.5 py-0.5 text-detail font-medium text-skill">
                            Uses Mora Pro — costs more per edit
                          </span>
                        ) : null}
                      </div>
                      <Textarea
                        value={editPrompt}
                        onChange={(e) => setEditPrompt(e.target.value)}
                        placeholder="Describe a change…"
                        rows={2}
                        maxRows={8}
                        className="w-full rounded-md border-border/60 px-2 py-1.5 text-sm text-foreground focus-visible:border-ring/60"
                      />
                      <Button
                        size="sm"
                        variant="neutral"
                        className="w-full"
                        disabled={editFrozen || !editPrompt.trim()}
                        onClick={() => runDescribeEdit(index)}
                      >
                        <SparkleIcon className="size-3.5" weight="bold" />
                        Apply edit
                      </Button>
                    </PopoverContent>
                  </Popover>
                )}

                <CircleIconButton
                  size="sm"
                  onClick={() => remove(index)}
                  disabled={reviewLocked}
                  ariaLabel="Remove media"
                  className={cn(
                    THUMB_ACTION_CHIP,
                    'absolute right-1 top-1',
                    reviewLocked
                      ? 'pointer-events-none opacity-40'
                      : 'text-muted-foreground hover:bg-background hover:text-destructive',
                  )}
                >
                  <XIcon className="size-3" weight="bold" />
                </CircleIconButton>

                {/* Reorder only when there's actually more than one slide —
                  disabled arrows on a single-image post are dead chrome
                  (Subtraction). */}
                {mediaUrls.length > 1 ? (
                  <>
                    <CircleIconButton
                      size="sm"
                      ariaLabel="Move left"
                      disabled={index === 0 || reviewLocked}
                      onClick={() => move(index, -1)}
                      className={cn(
                        THUMB_ACTION_CHIP,
                        'absolute left-1 bottom-1 text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-30',
                      )}
                    >
                      <ArrowLeftIcon className="size-3" weight="bold" />
                    </CircleIconButton>
                    <CircleIconButton
                      size="sm"
                      ariaLabel="Move right"
                      disabled={index === mediaUrls.length - 1 || reviewLocked}
                      onClick={() => move(index, 1)}
                      className={cn(
                        THUMB_ACTION_CHIP,
                        'absolute right-1 bottom-1 text-muted-foreground hover:bg-background hover:text-foreground disabled:opacity-30',
                      )}
                    >
                      <ArrowRightIcon className="size-3" weight="bold" />
                    </CircleIconButton>
                  </>
                ) : null}
              </div>
            );
          })}

          {/* Add tile — split affordance: upload a new file, or attach an
            already-uploaded item from the Library/Project media picker. */}
          <Popover open={openAddMenu} onOpenChange={setOpenAddMenu}>
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={isUploading}
                aria-label="Add media"
                className="inline-flex size-20 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-border/60 bg-muted/20 text-muted-foreground transition-colors hover:border-border hover:bg-muted/40 hover:text-foreground disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {isUploading ? (
                  <SparkleIcon className="size-4 animate-pulse" weight="duotone" />
                ) : (
                  <>
                    <PlusIcon className="size-4" weight="bold" />
                    <span className="text-micro">Add</span>
                  </>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-56 space-y-0.5 p-1.5">
              <button
                type="button"
                onClick={handleAddClick}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted/50"
              >
                <UploadSimpleIcon
                  className="size-4 shrink-0 text-muted-foreground"
                  weight="regular"
                />
                Upload new
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpenAddMenu(false);
                  // This is a plain APPEND entry point, not a replace flow —
                  // clear any stale replace target left behind by an
                  // abandoned "Choose from library" (pencil → replace)
                  // interaction, or a picked/closed-without-picking drawer
                  // would silently overwrite that slot instead of appending.
                  replaceIndexRef.current = null;
                  setLibraryDrawerDefaultTab('library');
                  setLibraryDrawerOpen(true);
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted/50"
              >
                <ImagesSquareIcon
                  className="size-4 shrink-0 text-muted-foreground"
                  weight="regular"
                />
                From library
              </button>
            </PopoverContent>
          </Popover>

          {/* Generate-a-slide tile — AI-generates a NEW image cohesive with the
            existing ones (multi-seed → grows a single image into a carousel).
            AI-temporary surface contract: dashed skill-purple. */}
          <Popover
            open={openGenerate}
            onOpenChange={(next) => {
              setOpenGenerate(next);
              if (!next) setGeneratePrompt('');
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={isGenerating}
                aria-label="Generate a slide"
                className="inline-flex size-20 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-skill/40 bg-skill/[0.04] text-skill/80 transition-colors hover:border-skill/60 hover:bg-skill/10 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {isGenerating ? (
                  <SparkleIcon className="size-4 animate-pulse" weight="duotone" />
                ) : (
                  <>
                    <SparkleIcon className="size-4" weight="duotone" />
                    <span className="text-micro">Generate</span>
                  </>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-2 p-3">
              <div className="flex items-center gap-1.5">
                <SparkleIcon className="size-3 text-skill/70" weight="duotone" />
                <span className="text-detail font-medium text-skill/80">
                  {hasMedia ? 'Generate a cohesive new slide' : 'Generate an image'}
                </span>
              </div>
              {hasMedia ? (
                <p className="text-micro text-muted-foreground/70">
                  Matches the style of the current {mediaUrls.filter((u) => !isVideoUrl(u)).length}{' '}
                  image(s) and adds it as a new slide.
                </p>
              ) : null}
              <div className="flex flex-wrap gap-1.5">
                {[
                  {
                    label: 'Close-up detail',
                    prompt: 'A close-up detail shot, complementary to the existing images.',
                  },
                  {
                    label: 'Lifestyle scene',
                    prompt: 'A lifestyle scene with the subject in use, complementary to the set.',
                  },
                  {
                    label: 'Different angle',
                    prompt: 'The same subject from a different angle, complementary to the set.',
                  },
                ].map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    disabled={isGenerating}
                    onClick={() => void generateSlide(chip.prompt)}
                    className={cn(
                      QUIET_ACTION,
                      'h-7 px-1 text-detail text-skill/90 hover:text-skill',
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <Textarea
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                placeholder="Describe the new slide…"
                rows={2}
                maxRows={8}
                className="w-full rounded-md border-border/60 px-2 py-1.5 text-sm text-foreground focus-visible:border-ring/60"
              />
              <Button
                size="sm"
                variant="neutral"
                className="w-full"
                disabled={isGenerating || !generatePrompt.trim()}
                onClick={() => void generateSlide(generatePrompt)}
              >
                <SparkleIcon className="size-3.5" weight="bold" />
                Generate slide
              </Button>
            </PopoverContent>
          </Popover>
        </div>

        {/* Slot-scoped review surface (spec §5.4 / §7): an IN-BODY toggle, never a
          second dialog. Exactly one of pendingCompare / candidates is active at a
          time (the hook holds a single review), keyed to one carousel slot; the
          other thumbnails dim above. Rendered under the strip so the edited slot
          reads as "this one". */}
        {pendingCompare ? (
          <MediaCompareSlot
            compare={pendingCompare}
            busy={busySlot === pendingCompare.slotIndex}
            layout={mediaUrls.length > 1 ? 'stacked' : 'side-by-side'}
            onKeep={() => void keepRevised()}
            onRevert={() => void revertCompare()}
          />
        ) : null}
        {candidates ? (
          <VariationCandidates
            items={candidates.items}
            busy={busySlot === candidates.slotIndex}
            onPick={(c) => void pickCandidate(candidates.slotIndex, c)}
            onDismiss={dismissCandidates}
            onSeeMore={
              candidates.items.length === 1
                ? () =>
                    void generateVariations(candidates.slotIndex, candidates.items[0].prompt, 4, {
                      keepIdentity,
                    })
                : undefined
            }
          />
        ) : null}

        {onAltTextChange && mediaUrls[selectedIndex] && !isVideoUrl(mediaUrls[selectedIndex]) ? (
          <AltTextField
            key={childIds?.[selectedIndex] ?? selectedIndex}
            slideNumber={mediaUrls.length > 1 ? selectedIndex + 1 : undefined}
            value={altTexts?.[selectedIndex] ?? ''}
            onCommit={(next) => onAltTextChange(selectedIndex, next)}
            disabled={reviewLocked}
          />
        ) : null}

        {!hasMedia && !hasLinkCard ? (
          <p className="text-detail text-muted-foreground/70">
            No media yet. Add an image or video so this post has something to show.
          </p>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          accept={`image/*,video/mp4,video/quicktime,video/webm${allowDocuments ? `,${DOCUMENT_ACCEPT}` : ''}`}
          className="hidden"
          onChange={handleFileSelected}
        />

        {libraryDrawerOpen ? (
          <Suspense fallback={null}>
            <MediaDrawer
              isOpen={libraryDrawerOpen}
              onClose={() => setLibraryDrawerOpen(false)}
              defaultTab={libraryDrawerDefaultTab}
              selectionMode="single"
              onSelect={(url) => void handleLibraryUrl(url)}
            />
          </Suspense>
        ) : null}
      </section>
    );
  },
);
PostMediaManager.displayName = 'PostMediaManager';

// 128 (the retina tier for a 64px display size) covers this strip's size-20
// (80px) cells with room to spare — was fetching the same full-resolution
// asset as the main preview for a cell 1/10th its area, which is why this
// strip visibly lagged behind everything else in the dialog.
function MediaThumb({ url }: { url: string }) {
  if (isLinkedInDocumentUrl(url)) {
    const extension = (url.split(/[?#]/)[0].split('.').pop() ?? 'file').toUpperCase();
    return (
      <div
        className="size-full flex flex-col items-center justify-center gap-1 bg-muted"
        title={linkedInDocumentTitleFromUrl(url)}
      >
        <FileTextIcon className="size-6 text-muted-foreground" weight="regular" />
        <span className="text-micro text-muted-foreground">{extension}</span>
      </div>
    );
  }
  if (isVideoUrl(url)) {
    return (
      <video
        src={`${url}#t=0.5`}
        muted
        playsInline
        preload="metadata"
        className="size-full object-cover"
      />
    );
  }
  return (
    <OptimizedImage
      src={url}
      width={128}
      quality={60}
      className="size-full"
      lqip={false}
      // A broken/404 thumbnail degrades to a muted placeholder rather than
      // the browser's broken-image glyph, which would sit inside the
      // editable strip and read as a hard error.
      fallback={
        <div className="size-full flex items-center justify-center bg-muted">
          <SparkleIcon className="size-5 text-muted-foreground/40" />
        </div>
      }
    />
  );
}
